import { FlattenType } from "@/types/flatten-type";
import { AddUndefinedUnionProps } from "@/types/union-props";
import { Link, type NavigateOptions } from "@tanstack/react-router";
import type { VariantProps } from "cva";
import { cva } from "cva";
import { AnimatePresence, motion } from "framer-motion";
import { LoaderCircle } from "lucide-react";
import React, { type ComponentPropsWithRef } from "react";
import { Button as RAButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps, focusRing } from "./utils";

const styles = cva({
  base: [
    "min-w-40 inline-flex items-center justify-center whitespace-nowrap rounded-md cursor-pointer gap-2",
    "transition-[color,background-color,border-color,opacity,scale] duration-300",
    "disabled-or-aria-disabled:opacity-50 disabled-or-aria-disabled:cursor-not-allowed",
  ].join(" "),
  variants: {
    intent: {
      primary: "text-theme-on-primary bg-linear-(--theme-gradient)",
      secondary:
        "text-theme-on-background border border-theme-outline-variant hover:not-disabled-or-aria-disabled:bg-theme-on-background-hover hover:not-disabled-or-aria-disabled:border-theme-outline",
      tertiary:
        "text-theme-on-background hover:not-disabled-or-aria-disabled:bg-theme-on-background-hover",
    },
    size: {
      rounded: "rounded-full",
      sm: "h-7 px-4 py-1 text-xs",
      /**
       * ? NOTE - 30.06.2025
       * ? Sean & Josefine agreed to just use "lg" since there were very few "md" buttons, and they were only 4px smaller than the "lg" ones.
       * ? If we want both "md" & "lg", we should make the size difference more noticeable and intentional.
       */
      // md: "h-11 px-6 py-1.5 text-sm",
      lg: "xl:h-12 h-10 px-6 text-sm",
      input: "h-[78px] px-8 text-sm", // made to match our input height
    },
    animate: {
      true: "hover:not-disabled-or-aria-disabled:scale-97",
    },
  },
  defaultVariants: {
    intent: "primary",
    size: "lg",
    animate: false,
  },
});

function ButtonChildren({
  children,
  isPending,
}: {
  children: React.ReactNode;
  isPending?: boolean;
}) {
  return (
    <div className="relative">
      <div className="invisible">{children}</div>
      <AnimatePresence mode="wait" initial={false}>
        {isPending ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: 0.15,
            }}
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <LoaderCircle className="size-4 animate-spin" />
          </motion.div>
        ) : (
          <motion.div
            key="children"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: 0.15,
            }}
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

type CommonProps = FlattenType<
  {
    className?: string;
    isDisabled?: boolean;
    children?: React.ReactNode;
  } & VariantProps<typeof styles>
>;

type ButtonElementProps = FlattenType<
  Omit<ComponentPropsWithRef<typeof RAButton>, keyof CommonProps>
>;

type LinkProps = FlattenType<
  {
    link: NavigateOptions;
  } & Omit<
    ComponentPropsWithRef<typeof Link>,
    keyof CommonProps | keyof NavigateOptions | "disabled"
  >
>;

type AnchorOptions = Pick<React.JSX.IntrinsicElements["a"], "href" | "target">;

type AnchorProps = FlattenType<
  {
    anchor: AnchorOptions;
  } & Omit<
    React.JSX.IntrinsicElements["a"],
    keyof CommonProps | keyof AnchorOptions
  >
>;

export type ButtonProps = FlattenType<
  CommonProps &
    AddUndefinedUnionProps<ButtonElementProps | LinkProps | AnchorProps>
>;

export function Button({
  className,
  animate,
  size,
  intent,
  isDisabled,
  children,
  ...props
}: ButtonProps) {
  const baseClassName = twMerge(
    styles({
      animate,
      size,
      intent,
    }),
    className,
  );

  if (props.anchor) {
    const { anchor, ref, ...rest } = props;
    return (
      <a
        {...rest}
        ref={ref}
        className={twMerge(
          focusRing({ isFocusVisible: false }),
          "focus-visible:outline-2",
          baseClassName,
        )}
        {...(isDisabled ? { "aria-disabled": true } : anchor)}
      >
        {children}
      </a>
    );
  }

  if (props.link) {
    const { link, ref, ...rest } = props;
    return (
      <Link
        {...rest}
        {...link}
        ref={ref}
        className={twMerge(
          focusRing({ isFocusVisible: false }),
          "focus-visible:outline-2",
          baseClassName,
        )}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      >
        {children}
      </Link>
    );
  }

  const { isPending, ref, ...rest } = props;

  return (
    <RAButton
      {...rest}
      ref={ref}
      className={composeTailwindRenderProps(focusRing, baseClassName)}
      isDisabled={isDisabled}
      isPending={isPending}
    >
      <ButtonChildren isPending={isPending}>{children}</ButtonChildren>
    </RAButton>
  );
}
